import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import PredictionCard from './PredictionCard';

function getAuthHeader() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const PERIODS = [
  { value: 'max', label: 'Max' },
  { value: '7d', label: '7D' },
  { value: '1m', label: '1M' },
  { value: '3m', label: '3M' },
  { value: '6m', label: '6M' },
  { value: '1y', label: '1Y' },
];

function ReturnBar({ value }) {
  if (value == null) return <span className="text-xs text-gray-400">—</span>;
  const capped = Math.min(Math.abs(value), 50);
  const pct = (capped / 50) * 100;
  const positive = value >= 0;
  return (
    <div className="flex items-center gap-2">
      <span className={`text-xs font-medium w-14 text-right ${positive ? 'text-emerald-600' : 'text-red-500'}`}>
        {positive ? '+' : ''}{value.toFixed(1)}%
      </span>
      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden max-w-[120px]">
        <div
          className={`h-full rounded-full ${positive ? 'bg-emerald-500' : 'bg-red-400'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function SectorRow({ row, slug, groupBy, period }) {
  const [expanded, setExpanded] = useState(false);
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(false);

  function toggle() {
    if (!expanded && predictions.length === 0) {
      setLoading(true);
      const param = groupBy === 'industry' ? 'industry' : 'sector';
      axios
        .get(
          `${API_BASE_URL}/api/research/analysts/${slug}/predictions/?${param}=${encodeURIComponent(row.label)}`,
          { headers: getAuthHeader() },
        )
        .then((res) => setPredictions(Array.isArray(res.data) ? res.data : res.data.results ?? []))
        .catch(() => setPredictions([]))
        .finally(() => setLoading(false));
    }
    setExpanded((v) => !v);
  }

  return (
    <div className="border border-gray-100 rounded-xl overflow-hidden">
      <button
        onClick={toggle}
        className="w-full flex items-center gap-3 px-4 py-3 bg-white hover:bg-gray-50 transition-colors text-left"
      >
        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium text-gray-800 truncate">{row.label}</span>
        </div>
        <span className="text-xs text-gray-400 w-16 text-right">{row.calls} calls</span>
        <div className="w-44">
          <ReturnBar value={row.avg_return} />
        </div>
        <span className="text-gray-300 ml-1">{expanded ? '▲' : '▼'}</span>
      </button>

      {expanded && (
        <div className="border-t border-gray-100 bg-gray-50 p-4">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-28 bg-white rounded-xl animate-pulse" />
              ))}
            </div>
          ) : predictions.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-4">No predictions found.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {predictions.map((p) => (
                <PredictionCard key={p.id} prediction={p} returnPeriod={period} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function SectorEdgeTab({ slug }) {
  const [rows, setRows] = useState([]);
  const [groupBy, setGroupBy] = useState('sector');
  const [period, setPeriod] = useState('max');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API_BASE_URL}/api/research/analysts/${slug}/sector-edge/`, {
        params: { group_by: groupBy, period },
      })
      .then((res) => setRows(res.data.rows ?? []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [slug, groupBy, period]);

  return (
    <div>
      {/* Controls */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="flex gap-1">
          {PERIODS.map(({ value, label }) => (
            <button
              key={value}
              onClick={() => setPeriod(value)}
              className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                period === value
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1 ml-auto">
          <span className="text-xs text-gray-400 mr-1">Group by:</span>
          {['sector', 'industry'].map((g) => (
            <button
              key={g}
              onClick={() => setGroupBy(g)}
              className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors capitalize ${
                groupBy === g
                  ? 'bg-gray-900 text-white border-gray-900'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
              }`}
            >
              {g}
            </button>
          ))}
        </div>
      </div>

      {/* Column header */}
      <div className="flex items-center gap-3 px-4 py-2 text-xs text-gray-400 font-medium">
        <div className="flex-1">Segment</div>
        <span className="w-16 text-right">Calls</span>
        <span className="w-44">Return</span>
        <span className="w-4" />
      </div>

      {loading ? (
        <div className="flex flex-col gap-2">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-12 bg-white rounded-xl border border-gray-100 animate-pulse" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center py-10 text-gray-400 bg-white rounded-xl border border-gray-100">
          No sector data available yet.
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {rows.map((row) => (
            <SectorRow key={row.label} row={row} slug={slug} groupBy={groupBy} period={period} />
          ))}
        </div>
      )}
    </div>
  );
}
